import { GetServerSidePropsContext, InferGetServerSidePropsType } from "next";
import { useRouter } from "next/router";
import { useEffect } from "react";

import Container from "~/components/common/container";
import Grid from "~/components/common/grid";
import CustomerCare from "~/components/customer-care/customer-care";
import LayoutPage from "~/components/layout/layout-page";
import CreateAccount from "~/components/login/create-account";
import Login from "~/components/login/login";
import { useAuthContext } from "~/contexts/auth";
import { usePageTracking } from "~/contexts/page-trackings";
import Logger from "~/services/logger/logger";
import { LoginSignUpOrigins } from "~/shared/user/login-sign-up-origins.enum";
import styles from "~/styles/pages/login.module.scss";
import appRoutes from "~/utils/app-routes";
import ssrUtils from "~/utils/ssr-utils";

type Props = InferGetServerSidePropsType<typeof getServerSideProps>;

export default function LoginPage(props: Props) {
  const { ...rest } = props;
  const router = useRouter();
  const { isLoadingUser, isAuthenticated } = useAuthContext();
  const redirectsMap: Partial<Record<LoginSignUpOrigins, string>> = {
    cart: appRoutes.CHECKOUT,
    checkout: appRoutes.CHECKOUT,
  };

  usePageTracking("login", { title: props.page?.seoMetadata?.fields.seoTitle });

  // redirect for logged users
  useEffect(() => {
    const redirects = Object.entries(redirectsMap).reduce((acc, [k, v]) => {
      acc[k] = new URL(v, location.origin);
      acc[k].searchParams.set("origin", k);

      return acc;
    }, {} as Record<string, URL>);
    Logger.instance.debug("login: checking auth status", {
      isLoadingUser,
      isAuthenticated,
    });
    if (router && !isLoadingUser && isAuthenticated) {
      Logger.instance.debug("login: redirect to origin or myaccount");
      router.replace(redirects[router.query.origin as string]?.href || appRoutes.MYACCOUNT);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, router, isLoadingUser]);

  return (
    <LayoutPage {...rest} mainClasses={styles.pageBackground} navColor="white" hidePrefooter>
      <Container>
        <Grid className={styles.grid}>
          <div className={styles.containerLogin}>
            <Login />
          </div>
          <div className={styles.containerCreateAccount}>
            <CreateAccount
              benefits={props.page?.accountBenefitList ?? []}
              loyaltyProgramLink={props.page?.loyaltyProgramLink}
            />
          </div>
          <div className={styles.containerCustomerCare}>
            <CustomerCare />
          </div>
        </Grid>
      </Container>
    </LayoutPage>
  );
}

export async function getServerSideProps(context: GetServerSidePropsContext) {
  if (context.locale === context.defaultLocale) {
    return {
      notFound: true,
    };
  }

  const pageData = await ssrUtils.getServerSideLoginPageData(context);

  return {
    props: {
      ...pageData,
    },
  };
}
